import { PatientViewModel } from '../../patient/components/models/patient-view-model';
import { checkDemographicsComplete, getMissingDemographicFields } from './validation-checks';
import { disabledConvertMessages } from '../../shared/enums';
import { emailAddressValidator } from '../../shared/misc/regex';

export interface PrePayRequirement {
  requirementName: string;
  status: boolean;
  missingFields?: string[];
  fixUrl?: string;
  fixLabel?: string;
}

export interface PrePayValidationParams {
  patient: PatientViewModel | null;
  patientId?: number;
  billingEmail?: string;
}

export interface PrePayValidationResult {
  isValid: boolean;
  requirements: PrePayRequirement[];
  failedRequirements: PrePayRequirement[];
}

/**
 * Check if billing/receipt email is valid.
 */
const checkBillingEmailComplete = (email?: string): boolean => {
  if (!email || email.trim() === '') return false;
  return emailAddressValidator.test(email);
};

/**
 * Get missing billing email field description.
 */
const getMissingBillingEmailFields = (email?: string): string[] => {
  if (!email || email.trim() === '') return ['Receipt Email'];
  if (!emailAddressValidator.test(email)) return ['Valid Receipt Email'];
  return [];
};

export const validatePrePayRequirements = (
  params: PrePayValidationParams
): PrePayValidationResult => {
  const { patient, patientId, billingEmail } = params;

  const demographicsStatus = checkDemographicsComplete(patient);
  const billingEmailStatus = checkBillingEmailComplete(billingEmail);

  // Build fix URL if patientId is provided
  const specialistDashboardUrl = patientId ? `/specialist/dashboard/${patientId}` : undefined;

  const requirements: PrePayRequirement[] = [
    {
      requirementName: disabledConvertMessages.demographicsLabel,
      status: demographicsStatus,
      missingFields: !demographicsStatus ? getMissingDemographicFields(patient) : undefined,
      fixUrl: specialistDashboardUrl,
      fixLabel: 'Complete',
    },
    {
      requirementName: 'Receipt email is required',
      status: billingEmailStatus,
      missingFields: !billingEmailStatus ? getMissingBillingEmailFields(billingEmail) : undefined,
    },
  ];

  const failedRequirements = requirements.filter((req) => !req.status);
  const isValid = failedRequirements.length === 0;

  return {
    isValid,
    requirements,
    failedRequirements,
  };
};

export const arePrePayRequirementsMet = (
  params: PrePayValidationParams
): boolean => {
  return validatePrePayRequirements(params).isValid;
};
